import { useBoolean } from 'ahooks';
import React, { FC, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';
import { AccountStatus } from 'api/account';
import { PermissionSetType } from 'api/models';
import {
  SubscriptionRenewalFailedBanner,
  TrialExpiryBanner,
} from 'components/molecules/account-expiry-banner';
import { getPlanName } from 'components/organisms/module-settings/billing/utils';
import { RoutePaths } from 'constant';
import { useUserPermissionSetType } from 'hooks/permissions';
import {
  useAccount,
  useBillingFeatureFlag,
  useIsAccountActive,
  useIsAccountActiveWithoutBilling,
} from 'hooks/use-account';
import { useAccountDataQuery } from 'queries/account';
import { getDevEnv } from 'utils/is-localhost';
import { SubscriptionExpired, TrialExpired } from './account-expired-pages';
import { BuyPage } from './buy';

export const AccountAssessment: FC = ({ children }) => {
  const history = useHistory();
  const { expiryDate, status } = useAccount();
  const { data: accountData } = useAccountDataQuery();
  const isAccActive = useIsAccountActive();
  const { isLocal } = getDevEnv();
  const isAccActiveWithoutBilling = useIsAccountActiveWithoutBilling();

  const currentUserPermissionSetType = useUserPermissionSetType();
  const isAccountAdmin = useMemo(
    () => currentUserPermissionSetType === PermissionSetType.AccountAdmin,
    [currentUserPermissionSetType],
  );
  const isTrialExpired = useMemo(
    () => !isAccActiveWithoutBilling && status === AccountStatus.Trial,
    [status, isAccActiveWithoutBilling],
  );

  const currentPlan = useMemo(
    () =>
      accountData?.billingMeta?.subscription
        ? getPlanName(accountData.billingMeta.subscription.plan.planName)
        : null,
    [accountData],
  );

  const [canShowForm, { setTrue: showForm, setFalse: closeForm }] =
    useBoolean(false);
  const isBillingEnabled = useBillingFeatureFlag();

  const onBuy = useCallback(() => {
    if (isBillingEnabled && isAccountAdmin) {
      history.push(RoutePaths.billingSettings);
    } else {
      showForm();
    }
  }, [history, isAccountAdmin, isBillingEnabled, showForm]);

  if (expiryDate == null) {
    return <>{children}</>;
  }

  return (
    <>
      {!isLocal ? (
        <>
          <TrialExpiryBanner onBuy={onBuy} />
          <SubscriptionRenewalFailedBanner />
        </>
      ) : null}

      {!isBillingEnabled ? (
        canShowForm || isTrialExpired ? (
          <BuyPage isExpired={isTrialExpired} handleClose={closeForm} />
        ) : (
          children
        )
      ) : isAccActive || isAccountAdmin ? (
        children
      ) : isTrialExpired ? (
        <TrialExpired currentPlan={currentPlan} />
      ) : (
        <SubscriptionExpired />
      )}
    </>
  );
};
